import prettyBytes from 'pretty-bytes';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useConversationStoreShallow } from 'src/store/conversationStore';

import { IconManager, IconName } from '@speeki/icons';

import { TooltipWithQuestionMark } from '@components/molecules/TooltipWithQuestionMark/TooltipWithQuestionMark';

import { FileUpload } from '@domains/new-conversation/atoms/FileUpload/FileUpload';
import {
    BorderWrapper,
    CloseIcon,
    InfoText,
    List,
    ListItem,
    ReportUploadWrapper,
    Text,
    UploadText,
    UploadTextWrapper,
} from '@domains/new-conversation/organisms/ReportUpload/ReportUpload.styled';
import { useReportUpload } from '@domains/new-conversation/organisms/ReportUpload/useReportUpload';
import {
    ConversationText,
    ConversationTitle,
} from '@domains/new-conversation/pages/Conversation.styled';
import {
    ALL_FORMATS,
    AUDIO_FORMATS,
    IMAGE_FORMATS,
    VIDEO_FORMATS,
} from '@shared/constants';

export const ReportUpload = () => {
    const { t } = useTranslation();
    const { caseUuid } = useParams<{ caseUuid: string }>();

    const { files, handleUpload, loading, removeFile, uploadedFiles } =
        useReportUpload(caseUuid ?? '');

    const { loading: conversationLoading } = useConversationStoreShallow([
        'loading',
    ]);

    const getIcon = (fileName: string): IconName => {
        const parts = fileName.split('.');
        const extension = parts[parts.length - 1];

        if (AUDIO_FORMATS.includes(extension)) return 'SvgFileSound24';
        if (IMAGE_FORMATS.includes(extension)) return 'SvgImage24';
        if (VIDEO_FORMATS.includes(extension)) return 'SvgFileVideo24';
        return 'SvgFileBig24';
    };

    return (
        <>
            <ConversationTitle>{t('REPORT_EVIDENCE_UPLOAD')}</ConversationTitle>
            <ConversationText>{t('REPORT_EVIDENCE')}</ConversationText>
            <ReportUploadWrapper>
                <FileUpload
                    disabled={loading || conversationLoading}
                    handleChange={handleUpload}
                    name={'file'}
                />
                <UploadTextWrapper>
                    <UploadText>{t('REPORT_EVIDENCE_UPLOAD_SIZE')}</UploadText>
                    <TooltipWithQuestionMark
                        testId="Okhi3xj"
                        text={`${t('SUPPORTED_FILES_FORMATS')}
                    ${ALL_FORMATS.join(', ').toUpperCase()}`}
                    />
                </UploadTextWrapper>

                {uploadedFiles.length ? (
                    <List>
                        {uploadedFiles?.map((file) => {
                            return (
                                <ListItem key={file.uuid}>
                                    <IconManager
                                        icon={getIcon(file.name)}
                                        size={24}
                                    />
                                    <BorderWrapper>
                                        <Text>{file.name}</Text>
                                        <InfoText>
                                            •{` ${prettyBytes(file.size || 0)}`}
                                        </InfoText>
                                        <CloseIcon
                                            iconProps={{
                                                icon: 'SvgClose24',
                                                size: 24,
                                                themeColors: {
                                                    default: {
                                                        'color-1':
                                                            'text.assistive',
                                                    },
                                                },
                                            }}
                                            onClick={() =>
                                                removeFile(file.uuid)
                                            }
                                            testId="8pRq01L"
                                        />
                                    </BorderWrapper>
                                </ListItem>
                            );
                        })}
                    </List>
                ) : null}
                {files.length ? (
                    <List>
                        {files?.map((file, index) => {
                            return (
                                <ListItem key={index}>
                                    <IconManager
                                        icon={getIcon(file.file.name)}
                                        size={24}
                                    />
                                    <BorderWrapper>
                                        <Text>{file.file.name}</Text>
                                        <InfoText>
                                            •{' '}
                                            {` ${prettyBytes(file.file.size)}`}
                                        </InfoText>
                                    </BorderWrapper>
                                </ListItem>
                            );
                        })}
                    </List>
                ) : null}
            </ReportUploadWrapper>
        </>
    );
};
